/**
 * Add and remove the files and screenshots on an issue (L-1224 part 2), at any stage.
 *
 * Adding is two steps: the browser uploads the file to Storage (storage.rules: create-only, 20 MB, no html/svg/xml),
 * then registers it with issueTriage_addAttachment, which checks the real Storage object before it writes anything.
 * A registration that fails (twice) leaves the uploaded object unattached; the person is told so, in those words.
 */
import { useCallback, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { httpsCallable } from "../../lib/firebase/authReadyCallable";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { functions, storage } from "../../config/firebase";
import { invokeNamedCallable } from "../../lib/telemetry/namedCallable";
import { useAuth } from "../../contexts/AuthContext";
import { checkUploadable, fileKey, storagePathFor } from "../../lib/issueTriage/issueFiles";
import { issueReportKeys, restoreIssue, snapshotIssue } from "./useIssueReports";
import type { IssueReport } from "../../types/issue-triage";

export function useAddIssueAttachment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ issueId, url, name }: { issueId: string; url: string; name: string }) => {
      const fn = httpsCallable(functions, "issueTriage_addAttachment");
      const res = await invokeNamedCallable("issueTriage_addAttachment", () =>
        fn({ organizationId: user?.organizationId, issueId, url, name }),
      );
      return { issueId, added: (res?.data as { added?: boolean } | undefined)?.added !== false };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: issueReportKeys.all });
    },
  });
}

export function useRemoveIssueAttachment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ issueId, url }: { issueId: string; url: string }) => {
      const fn = httpsCallable(functions, "issueTriage_removeAttachment");
      await invokeNamedCallable("issueTriage_removeAttachment", () =>
        fn({ organizationId: user?.organizationId, issueId, url }),
      );
      return { issueId };
    },
    onMutate: async ({ issueId, url }) => {
      await queryClient.cancelQueries({ queryKey: issueReportKeys.all });
      const before = snapshotIssue(queryClient, issueId);
      // Only files the team added can be removed (the server refuses the reporter's own); it matches by Storage path, so do the same.
      queryClient.setQueriesData({ queryKey: issueReportKeys.lists() }, (old: IssueReport[] | undefined) =>
        old?.map((i) =>
          i.id !== issueId ? i : { ...i, attachments: i.attachments?.filter((a) => fileKey(a.url) !== fileKey(url)) },
        ),
      );
      return { before };
    },
    onError: (_err, { issueId }, context) => {
      if (context?.before) restoreIssue(queryClient, issueId, context.before);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: issueReportKeys.all });
    },
  });
}

/** Server refusals that will never succeed on a retry (a full list, a denied caller, a rejected file). */
const PERMANENT_CODES = ["invalid-argument", "permission-denied", "resource-exhausted", "not-found", "failed-precondition", "unauthenticated"];

function errorCode(err: unknown): string {
  return String((err as { code?: unknown } | null)?.code ?? "").replace(/^functions\//, "");
}

/** What the server said, in its own words (its messages are written for people), trimmed to one line. */
function serverReason(err: unknown): string {
  // invokeNamedCallable keeps the server's own text on `serverMessage` (its `message` is only "<callable> failed: <code>").
  const e = err as { serverMessage?: unknown; message?: unknown } | null;
  const m = typeof e?.serverMessage === "string" && e.serverMessage !== "" ? e.serverMessage : e?.message;
  return typeof m === "string" ? m.replace(/\s+/g, " ").trim().slice(0, 160) : "";
}

export interface FileOutcome {
  name: string;
  ok: boolean;
  /** True when the file was already on the issue, so nothing was added (still not a failure). */
  already?: boolean;
  /** Plain words for the person when not ok. */
  message?: string;
}

export interface IssueFileUploader {
  upload: (files: File[]) => Promise<FileOutcome[]>;
  busy: boolean;
}

/** Uploads files for one issue, one at a time, and reports what happened to EACH (never one lump "failed"). */
export function useIssueFileUpload(issue: Pick<IssueReport, "id" | "attachments">): IssueFileUploader {
  const add = useAddIssueAttachment();
  const [busy, setBusy] = useState(false);

  const upload = useCallback(
    async (files: File[]): Promise<FileOutcome[]> => {
      setBusy(true);
      const outcomes: FileOutcome[] = [];
      let count = issue.attachments?.length ?? 0;
      try {
        for (const file of files) {
          const refusal = checkUploadable(file, count);
          if (refusal) {
            outcomes.push({ name: file.name, ok: false, message: refusal });
            continue;
          }
          let url: string;
          try {
            const path = storagePathFor(issue.id, file.name, Date.now(), Math.random().toString(36).slice(2));
            const objectRef = ref(storage, path);
            await uploadBytes(objectRef, file, { contentType: file.type || "application/octet-stream" });
            url = await getDownloadURL(objectRef);
          } catch {
            outcomes.push({ name: file.name, ok: false, message: `${file.name} could not be uploaded. Nothing was attached.` });
            continue;
          }
          // Registering is idempotent on the server, so one retry can never attach it twice. A refusal that can
          // never succeed (list full, not allowed, file rejected) is not retried and is shown as the server said it.
          let registered = false;
          let alreadyThere = false;
          let serverRefusal: string | null = null;
          for (let attempt = 0; attempt < 2 && !registered && serverRefusal === null; attempt += 1) {
            try {
              const res = await add.mutateAsync({ issueId: issue.id, url, name: file.name });
              registered = true;
              alreadyThere = !res.added;
            } catch (err) {
              if (PERMANENT_CODES.includes(errorCode(err))) serverRefusal = serverReason(err) || "the server refused it";
            }
          }
          if (registered && alreadyThere) {
            // The server found the same object already on the issue (as a team file, the capture or a reporter image).
            outcomes.push({ name: file.name, ok: true, already: true });
          } else if (registered) {
            count += 1;
            outcomes.push({ name: file.name, ok: true });
          } else if (serverRefusal !== null) {
            outcomes.push({ name: file.name, ok: false, message: `${file.name} was uploaded but could not be attached: ${serverRefusal}.` });
          } else {
            // No answer either time: the first call may well have landed. Adding it again would upload a second copy.
            outcomes.push({
              name: file.name,
              ok: false,
              message: `${file.name} was uploaded but we did not get an answer, so it may already be attached. Check the file list before adding it again.`,
            });
          }
        }
      } finally {
        setBusy(false);
      }
      return outcomes;
    },
    [add, issue.attachments?.length, issue.id],
  );

  return { upload, busy };
}
